/* eslint-disable react/no-array-index-key, function-paren-newline, react/no-unused-prop-types */
import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Field,
  tokens,
  Button,
  Text,
  Input,
  Combobox,
  Option,
  Card,
  CardHeader,
  CardFooter,
  shorthands,
} from '@fluentui/react-components';
import { Drawer, DrawerHeader, DrawerHeaderTitle, DrawerBody, DrawerFooter } from '@fluentui/react-drawer';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { Dismiss24Regular, Add24Regular, Delete24Regular, Delete16Regular } from '@fluentui/react-icons';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  TARGET_TYPE,
  TARGET_TYPE_MAP,
  CONDITION_OPERATOR,
  RULE_SEPERATOR,
  MAX_VALUE_LENGTH,
  RULE_TYPE,
} from '../helpers/constants';
import { extractTargetsFromViewModel, getPlaceHolderText, getTargetStrings, isCategoryOrCustomLabel } from '../helpers/utils';

export const getUrlRulesEditorStyle = () => ({
  rulesInputInnerContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0px',
    '& .target-dropdown': {
      minWidth: '161px',
      height: '32px',
    },
    '& .conditions': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '12px',
      marginLeft: '12px',
    },
    '& .condition': {
      display: 'flex',
      flexDirection: 'row',
      gap: '12px',
      '& .operator': {
        paddingTop: '6px',
        minWidth: '60px',
      },
    },
    '& .validated-input': {
      width: '231px',
      '& .ms-TextField-fieldGroup': {
        borderRadius: '4px',
      },
    },
  },

  cardHeader: {
    borderBottom: '1px solid var(--colorNeutralStroke1)',
    paddingBottom: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },

  verticalSpacing: {
    ...shorthands.margin(tokens.spacingVerticalS, '0'),
  },

  rulesListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  ruleCard: {
    flex: 1,
    width: '100%',
  },
});

const CUSTOM_DRAWER_WIDTH = 700;

export const UrlRulesEditor = ({ i18n, urlRules, onChange, isOpen, onClose }) => {
  const styles = useDefaultStyles(getUrlRulesEditorStyle);

  const [newAssetGroupUrlRules, setNewAssetGroupUrlRules] = useState(urlRules || []);
  const [conditions, setConditions] = useState([{ target: TARGET_TYPE.Url }]);
  const [errorMessages, setErrorMessages] = useState([]);

  const { urlTargets } = useMemo(() => extractTargetsFromViewModel(newAssetGroupUrlRules), [newAssetGroupUrlRules]);
  const isCustomLabelType = useMemo(() => conditions[0].target === TARGET_TYPE.CustomLabel, [conditions[0].target]);
  const isAnyConditionEmpty = useMemo(() => conditions.some(({ condition = '' }) => !condition.trim().length), [conditions]);

  const onAddRuleButtonClick = useCallback(() => {
    setConditions([...conditions, { target: isCustomLabelType ? TARGET_TYPE.CustomLabel : TARGET_TYPE.Url }]);
  }, [conditions, setConditions, isCustomLabelType]);

  const onCreateRuleButtonClick = useCallback(() => {
    setConditions([{ target: TARGET_TYPE.Url, condition: '' }]);

    const rule = {};
    conditions.forEach(({ target, condition }, index) => {
      const trimmedCondition = condition.trim();
      if (!trimmedCondition) return;
      rule[`TargetCondition${index + 1}`] = target;
      rule[`TargetConditionOperator${index + 1}`] = isCategoryOrCustomLabel(target) ? CONDITION_OPERATOR.Equals : CONDITION_OPERATOR.Contains;
      rule[`TargetValue${index + 1}`] = trimmedCondition;
    });

    const isEqual = (val1, val2) => (val1 == null && val2 == null) || val1 === val2;

    const ruleAlreadyExists = newAssetGroupUrlRules.some((existingRule) =>
        isEqual(rule.TargetCondition1, existingRule.TargetCondition1) &&
        isEqual(rule.TargetValue1, existingRule.TargetValue1) &&
        isEqual(rule.TargetCondition2, existingRule.TargetCondition2) &&
        isEqual(rule.TargetValue2, existingRule.TargetValue2) &&
        isEqual(rule.TargetCondition3, existingRule.TargetCondition3) &&
        isEqual(rule.TargetValue3, existingRule.TargetValue3)
    );
    if (ruleAlreadyExists) return;

    setNewAssetGroupUrlRules([...newAssetGroupUrlRules, rule]);
  }, [conditions, newAssetGroupUrlRules]);

  const targetTypeDropdownOptions = useMemo(() => [
    { key: TARGET_TYPE.Url, text: i18n.getString(_TL_('URL')), type: RULE_TYPE.Url },
    { key: TARGET_TYPE.PageTitle, text: i18n.getString(_TL_('Page title')), type: RULE_TYPE.Url },
    { key: TARGET_TYPE.PageContent, text: i18n.getString(_TL_('Page content')), type: RULE_TYPE.Url },
    { key: TARGET_TYPE.Category, text: i18n.getString(_TL_('Category')), type: RULE_TYPE.Url },
    { key: TARGET_TYPE.CustomLabel, text: i18n.getString(_TL_('Custom Label')), type: RULE_TYPE.CustomLabel },
  ], [i18n]);

  const updateValue = (value, index) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], condition: value };
    setConditions(newConditions);

    let validationMessage = '';
    if (value.length > MAX_VALUE_LENGTH) {
      validationMessage = i18n.getString(_TL_('Field should not exceed {{maxLength}} characters'), { maxLength: MAX_VALUE_LENGTH });
    }

    setErrorMessages((prevMessages) => {
      const newErrorMessages = [...prevMessages];
      newErrorMessages[index] = validationMessage;
      return newErrorMessages;
    });
  };

  const renderMessageBar = useCallback(() => (
    <MessageBar>
      <MessageBarBody>
        { i18n.getString(_TL_("URL condition types and custom labels can't be combined in the same rule.")) }
      </MessageBarBody>
    </MessageBar>
  ), [i18n]);

  const renderTargetTypeDropdownOptions = useCallback(() => targetTypeDropdownOptions.map((option) => (
    <Option key={option.key} value={option.key}>
      {option.text}
    </Option>
  )), [targetTypeDropdownOptions]);

  const renderTargetTypeDropdownOptionsByType = useCallback((type) => targetTypeDropdownOptions.map((option) => (
    <Option key={option.key} value={option.key} disabled={option.type !== type}>
      {option.text}
    </Option>
  )), [targetTypeDropdownOptions]);

  const renderRulesInput = useCallback(() => {
    const conditionInput = conditions.map(({ target, condition = '' }, index) => (
      <div className="condition">
        <Combobox
          key={index}
          className={classnames(`target-dropdown-${index + 1}`)}
          aria-label={i18n.getString(_TL_('Select a condition'))}
          value={TARGET_TYPE_MAP[target]}
          selectedOptions={[target]}
          onOptionSelect={(_, data) => {
            const newConditions = [...conditions];
            newConditions[index] = { ...newConditions[index], target: data.optionValue };
            setConditions(newConditions);
          }}
        >
          {conditions.length === 1 && renderTargetTypeDropdownOptions()}
          {conditions.length > 1 && renderTargetTypeDropdownOptionsByType(isCustomLabelType ? RULE_TYPE.CustomLabel : RULE_TYPE.Url)}
        </Combobox>

        <Text className="operator">{isCategoryOrCustomLabel(target) ? i18n.getString(_TL_('is')) : i18n.getString(_TL_('contains'))}</Text>

        <div className={classnames(styles.inputContainer, `condition-${index + 1}`)}>
          <Field
            validationState={errorMessages[index] ? 'error' : 'none'}
            validationMessage={errorMessages[index]}
          >
            <Input
              key={index}
              className="validated-input"
              value={condition}
              placeholder={getPlaceHolderText(i18n, target)}
              onChange={(event) => updateValue(event.target.value, index)}
            />
          </Field>
        </div>

        <Button
          className="delete-button"
          appearance="subtle"
          icon={<Delete24Regular />}
          aria-label={i18n.getString(_TL_('Delete condition'))}
          onClick={() => {
            if (conditions.length === 1) {
              setConditions([{ target: TARGET_TYPE.Url }]);
            } else {
              const newConditions = [...conditions];
              newConditions.splice(index, 1);
              setConditions(newConditions);
            }
          }}
        />
      </div>
    ));

    return (
      <div className={styles.rulesInputInnerContainer}>
        <div className="conditions">
          { conditionInput }
        </div>
      </div>
    );
  }, [conditions, styles.rulesInputInnerContainer, targetTypeDropdownOptions, renderTargetTypeDropdownOptions, isCustomLabelType, i18n, errorMessages]);

  const removeTargetTag = useCallback((targetString) => {
    const newRules = newAssetGroupUrlRules.filter((rule) => {
      return getTargetStrings(rule).join(RULE_SEPERATOR) !== targetString;
    });

    setNewAssetGroupUrlRules(newRules);
  }, [newAssetGroupUrlRules]);

  const renderTargets = useCallback((targets) => (
    <div className="rule-list">
        {targets.map((target) => {
          const targetString = target.join(RULE_SEPERATOR);

          return (
            <>
              <div className={styles.rulesListItem}>
                <Card className={classnames(styles.ruleCard, styles.verticalSpacing)} key={targetString} appearance="outline">
                  <CardHeader header={<Text>{targetString}</Text>} />
                </Card>
                <Button
                  appearance="subtle"
                  aria-label={i18n.getString(_TL_('Delete rule'))}
                  icon={<Delete16Regular />}
                  onClick={() => removeTargetTag(targetString)}
                />
              </div>
              <Text weight="semibold">{i18n.getString(_TL_('Or'))}</Text>
            </>
          );
        })}
    </div>
  ), [i18n, removeTargetTag]);

  const onSave = useCallback(() => {
    onChange({ AssetGroupUrlTargets: [...newAssetGroupUrlRules] });
    onClose();
  }, [newAssetGroupUrlRules, onChange, onClose]);

  const renderDrawerFooter = useCallback(() => (
    <>
      <Button className="apply-button" appearance="primary" onClick={onSave}>{i18n.getString(_TL_('Apply'))}</Button>
      <Button className="cancel-button" onClick={onClose}>{i18n.getString(_TL_('Cancel'))}</Button>
    </>
  ), [i18n, onClose, onSave]);

  return (
    <>
      <Drawer
        className="url-rules-editor-panel"
        open={isOpen}
        onOpenChange={onClose}
        position="end"
        style={{ width: CUSTOM_DRAWER_WIDTH }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={i18n.getString(_TL_('Close'))}
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {i18n.getString(_TL_('Create URL rules'))}
          </DrawerHeaderTitle>
          <Text>
              {i18n.getString(_TL_('Specify pages with URLs that contain a certain piece of text to target specific content, and use custom labels to define subsets of your page feeds for more precise and effective advertising campaigns.'))}
          </Text>
          <div className={styles.verticalSpacing}>{renderMessageBar()}</div>
        </DrawerHeader>
        <DrawerBody>
          {renderTargets(urlTargets)}

          <Card appearance="outline" className={styles.verticalSpacing}>
            <CardHeader className={styles.cardHeader} header={<Text weight="semibold">{i18n.getString(_TL_('Rule'))}</Text>} />
            {renderRulesInput()}
            <CardFooter>
              <Button
                className="add-rule-button"
                appearance="subtle"
                icon={<Add24Regular />}
                onClick={onAddRuleButtonClick}
                disabled={conditions.length > 2}
              >
                {i18n.getString(_TL_('And'))}
              </Button>
            </CardFooter>
          </Card>

          <div className={styles.verticalSpacing}>
            <Button
              className="create-rule-button"
              icon={<Add24Regular />}
              onClick={onCreateRuleButtonClick}
              disabled={isAnyConditionEmpty || errorMessages.some(messages => messages?.length > 0)}
            >
              {i18n.getString(_TL_('Create another rule'))}
            </Button>
          </div>
        </DrawerBody>
        <DrawerFooter>
          {renderDrawerFooter()}
        </DrawerFooter>
      </Drawer>
    </>
  );
};

UrlRulesEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  urlRules: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.string,
    TargetCondition1: PropTypes.string,
    TargetConditionOperator1: PropTypes.string,
    TargetValue1: PropTypes.string,
    TargetCondition2: PropTypes.string,
    TargetConditionOperator2: PropTypes.string,
    TargetValue2: PropTypes.string,
    TargetCondition3: PropTypes.string,
    TargetConditionOperator3: PropTypes.string,
    TargetValue3: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  enableSpecialCharactersValidation: PropTypes.bool,
};

UrlRulesEditor.defaultProps = {
  urlRules: [],
  enableSpecialCharactersValidation: false,
};
